import {
  useQuery,
  useMutation,
  useQueryClient,
  useInfiniteQuery,
} from 'react-query';
import { queryGet, queryUpdate } from './fetch';
import { Participant } from './participant';
import { PaginatedResults } from './participationCache';
import { CriterionListItem, ListValueType } from './recruitment';

export enum InvitationQueueStatus {
  PAUSED = 'PAUSED',
  ACTIVE = 'ACTIVE',
  STOPPED = 'STOPPED',
}

export interface InvitationQueue {
  id: string;
  recruitmentId: string;

  firstInvitationSentDate: Date;
  lastInvitationSentDate: Date;
  remainingResults: number;
  numberOfInvitationSent: number;

  scoringConfiguration: ScoringConfiguration;
  excludeAcceptedParticipations: boolean;

  status: InvitationQueueStatus;
  speed: number;
  handledCriteriaIds: string[];
  createdAt: Date;
  updatedAt: Date;
}

interface InvitationQueueSpeedInfo {
  missingTotal: number;
  speed: number;
  total: number;
}

export interface InvitationQueueMember extends Participant {
  id: string;
  searchScore: number;
  invitedAt?: Date;

  lastInvitation?: Date;
  lastParticipation?: Date;
  lastStillActiveAccepted?: Date;

  hasABookedAppointment: boolean;
}

export interface QuotaItem {
  criterionId: string;
  listValueType: string;
  item: string;
  listValueItem: CriterionListItem;
}

export interface QuotaItemWeightConfig {
  item: QuotaItem;
  weight: number;
  metadata?: any;
}

export interface CriterionConfig {
  enabled: boolean;
  allowEmpty: boolean;
  emptyMalus: number;
}

export interface DecayFunctionConfig {
  enabled: boolean;
  origin: number;
  decay: number;
  scale: number;
  type: string;
  weight: number;
}

export interface SingleValueConfig {
  enabled: boolean;
  weight: number;
  missingValue?: number;
}

export interface ScoringConfiguration {
  criteriaConfig: Record<string, CriterionConfig>;
  scoreMode: string;
  boostMode: string;
  malusOnRecentParticipations: DecayFunctionConfig;
  malusOnRecentInvitations: DecayFunctionConfig;
  malusOnStillAcceptedParticipation: DecayFunctionConfig;
  malusOnBookedAppointment: SingleValueConfig;
  participantScore: SingleValueConfig;
}

export interface QueueSearchResult<T> extends PaginatedResults<T> {
  handledCriteriaIds: string[];
  handledFields: string[];
}

export interface CriterionItemInfo {
  missing: number;
  weight?: number;
  missingWeight?: number;
  inPanelWeight?: number;
  normalizedWeight?: number;
  item: {
    listValueItem: CriterionListItem;
    item: string;
    criterionId: string;
    listValueType: ListValueType;
  };
  inPanelCount: number;
  completed: number;
  applied: number;
  planned: number;
  confirmed: number;

  required?: number;
}

export interface CriterionStateAndQuotaInfo {
  id: string;
  fieldRef: string;
  listValueType: ListValueType;
  screenerCriterion: boolean;
  missingCountInPanel?: number;
  allItemsInfos: CriterionItemInfo[];
}

export const useRecruitmentInvitationQueue = (recruitmentId?: string) => {
  const queryClient = useQueryClient();
  return useQuery<InvitationQueue>(
    ['recruitments', recruitmentId, 'invitation-queues'],
    queryGet(`/v2/recruitments/${recruitmentId}/invitation-queues`),
    {
      enabled: !!recruitmentId,
      onSuccess: (data) => {
        queryClient.setQueryData(['invitation-queues', data.id], data);
      },
    },
  );
};

export const useInvitationQueue = (id?: string) => {
  return useQuery<InvitationQueue>(
    ['invitation-queues', id],
    queryGet(`/v2/invitation-queues/${id}`),
    {
      enabled: !!id,
    },
  );
};

export const useInvitationQueueNextsToInvite = (
  id?: string,
  criteriaWeightWhitelist?: string[],
) => {
  return useInfiniteQuery<QueueSearchResult<InvitationQueueMember>>(
    ['invitation-queues', id, 'nexts', criteriaWeightWhitelist],
    ({ pageParam }) =>
      queryGet(`/v2/invitation-queues/${id}/nexts`, {
        cursor: pageParam,
        criteriaWeightWhitelist,
      })(),
    {
      enabled: !!id,
      getNextPageParam: (lastPage, allPages) => {
        const downloadedCount = allPages.reduce((acc, page) => {
          return acc + page.data.length;
        }, 0);
        if (downloadedCount >= lastPage.pagination.count) {
          return undefined;
        }
        return lastPage.pagination.cursor;
      },
    },
  );
};

export const useUpdateInvitationQueue = (id?: string) => {
  const queryClient = useQueryClient();
  return useMutation<
    InvitationQueue,
    unknown,
    {
      status?: InvitationQueueStatus;
      scoringConfiguration?: Partial<ScoringConfiguration>;
      excludeAcceptedParticipations?: boolean;
    }
  >(queryUpdate(`/v2/invitation-queues/${id}`), {
    onSuccess: (data) => {
      queryClient.invalidateQueries(['invitation-queues', data.id]);
      queryClient.setQueryData(['invitation-queues', data.id], data);
      queryClient.setQueryData(
        ['recruitments', data.recruitmentId, 'invitation-queues'],
        data,
      );
    },
  });
};

export const useInvitationQueueSpeedInfo = (id?: string) => {
  return useQuery<InvitationQueueSpeedInfo>(
    ['invitation-queues', id, 'speed'],
    queryGet(`/v2/invitation-queues/${id}/speed`),
    {
      enabled: !!id,
    },
  );
};

export const useCriterionStateInfo = (queueId?: string) => {
  return useQuery<CriterionStateAndQuotaInfo[]>(
    ['invitation-queues', queueId, 'criterion-state-infos'],
    queryGet(`/v2/invitation-queues/${queueId}/criterion-state-infos`),
    {
      enabled: !!queueId,
    },
  );
};

export const useSeveralQueuesRepartition = (queueId?: string) => {
  return useQuery<Array<{ criteriaWeightWhitelist: string[]; size: number }>>(
    ['invitation-queues', queueId, 'several-queues-repartition'],
    queryGet(`/v2/invitation-queues/${queueId}/several-queues-repartition`),
    {
      enabled: !!queueId,
    },
  );
};
